import { cn } from '@/lib/utils'
import React from 'react'

interface ResponsiveFormGridProps {
  children: React.ReactNode
  className?: string
}

/**
 * Responsive grid that switches from 1 column on mobile to 2 columns on larger screens
 */
export function ResponsiveFormGrid({ children, className }: ResponsiveFormGridProps) {
  return <div className={cn('grid grid-cols-1 sm:grid-cols-2 gap-4', className)}>{children}</div>
}

interface ResponsiveFormSectionProps {
  children: React.ReactNode
  className?: string
  fullWidth?: boolean
}

/**
 * Form section that can optionally span full width even in 2-column layout
 */
export function ResponsiveFormSection({
  children,
  className,
  fullWidth = false,
}: ResponsiveFormSectionProps) {
  return <div className={cn(fullWidth && 'sm:col-span-2', className)}>{children}</div>
}

interface ResponsiveDialogContentProps {
  children: React.ReactNode
  className?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
}

/**
 * Responsive dialog content wrapper with mobile-optimized sizing
 */
export function ResponsiveDialogContent({
  children,
  className,
  size = 'lg',
}: ResponsiveDialogContentProps) {
  const sizeClasses = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
    '2xl': 'sm:max-w-2xl',
  }

  return (
    <div
      className={cn(
        'w-full max-w-[calc(100vw-2rem)]', // Mobile: full width with margin
        sizeClasses[size],
        'max-h-[calc(100vh-4rem)] sm:max-h-[90vh]', // Mobile: more height, Desktop: 90vh
        'overflow-y-auto',
        'px-4 sm:px-6', // Smaller padding on mobile
        className
      )}
    >
      {children}
    </div>
  )
}

interface MobileScrollableFormProps {
  children: React.ReactNode
  className?: string
}

/**
 * Wrapper for forms that need to be scrollable on mobile with proper spacing
 */
export function MobileScrollableForm({ children, className }: MobileScrollableFormProps) {
  return (
    <div
      className={cn(
        'space-y-4',
        'pb-safe', // Safe area padding for iOS devices
        className
      )}
    >
      {children}
    </div>
  )
}

/**
 * Button group that stacks on mobile and aligns horizontally on desktop
 */
export function ResponsiveButtonGroup({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-col sm:flex-row gap-2 sm:gap-4',
        'pt-4 border-t', // Add visual separation
        className
      )}
    >
      {children}
    </div>
  )
}
